/**
 * @description Two-panel modal for composing and sending invoice or reminder emails
 *
 * Key features:
 * - Left panel: email controls (to, cc, bcc, subject, body, send-me-a-copy)
 * - Right panel: live email preview matching actual React Email template layout
 * - Mode-aware: `mode='invoice'` (default) or `mode='reminder'` — controls template,
 *   colors, labels, API call, and preview content via MODE_CONFIG lookup
 * - Pre-filled from company email template (via outreach templates API)
 * - Template variable interpolation with real invoice data
 * - Headless UI Dialog with enterprise modal pattern (unsaved changes confirm)
 * - Responsive: stacked layout on mobile, side-by-side on lg+
 * - Usable from InvoiceDetails, CreateInvoice, and EditInvoice pages
 *
 * @dependencies
 * - @headlessui/react: Dialog + Transition
 * - outreachService: fetch company template defaults
 * - invoiceService: send invoice / send reminder email
 * - useCompany: merchant display name for preview
 *
 * @notes
 * - From address is read-only (resolved server-side for security)
 * - "Send me a copy" pre-checked if company setting email_sender.send_copy_default is on
 * - Subject/body default from company_settings > setting_definitions cascade
 * - Template tokens interpolated with real invoice data in preview
 * - Reminder mode: amber theme, overdue badge in preview, no line items
 * - Follows enterprise modal pattern from .claude/context/patterns.md
 */

import React, { useState, useEffect, Fragment, useCallback, useMemo, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Send, Copy, Eye, EyeOff, Mail, Clock, CheckCircle2 } from 'lucide-react';
import outreachService from '../../services/api/outreachService';
import invoiceService from '../../services/api/invoiceService';
import settingsService from '../../services/api/settingsService';
import { useCompany } from '../../contexts/CompanyContext';
import { useBranding } from '../../contexts/BrandingContext';
import {
  formatCurrency,
  formatInvoiceDate,
  calendarDaysOverdue
} from '../../utils/invoiceHelpers';
import { resolveComposeFromEmail } from '../../utils/composeFromEmail';

// Template token → real invoice value mapping
const buildTokenMap = (invoice, company) => ({
  '{merchantName}': company?.businessName || 'Your Business',
  '{invoiceNumber}': invoice?.invoiceNumber || '',
  '{customerName}': invoice?.customerName || invoice?.customer?.name || 'Customer',
  '{dueDate}': invoice?.dueDate ? formatInvoiceDate(invoice.dueDate) : '',
  '{totalAmount}': formatCurrency(invoice?.totalAmount || 0)
});

const interpolate = (text, tokenMap) => {
  if (!text) return '';
  let result = text;
  Object.entries(tokenMap).forEach(([token, value]) => {
    result = result.replace(new RegExp(token.replace(/[{}]/g, '\\$&'), 'g'), value);
  });
  return result;
};

// Days overdue uses the company primary-location calendar, not the laptop TZ.

// Mode configuration — controls all theme/behavior differences between invoice & reminder
const MODE_CONFIG = {
  invoice: {
    templateKey: 'invoice',
    title: 'Send Invoice',
    successTitle: 'Invoice Sent',
    buttonLabel: 'Send Invoice',
    IconComponent: Mail,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    buttonBg: 'bg-gray-900 hover:bg-gray-800',
    spinnerBorder: 'border-brand',
    focusRing: 'focus:ring-brand focus:border-brand',
    defaultSubject: '{merchantName} | Invoice {invoiceNumber}',
    defaultBody: 'Please find your invoice below. Click the button to view and pay online.',
    apiCall: (invoiceNumber, payload) => invoiceService.sendInvoice(invoiceNumber, payload),
    errorPrefix: 'Failed to send invoice email'
  },
  reminder: {
    templateKey: 'reminder',
    title: 'Send Reminder',
    successTitle: 'Reminder Sent',
    buttonLabel: 'Send Reminder',
    IconComponent: Clock,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    buttonBg: 'bg-amber-600 hover:bg-amber-500',
    spinnerBorder: 'border-amber-500',
    focusRing: 'focus:ring-amber-500 focus:border-amber-500',
    defaultSubject: 'Reminder: Invoice {invoiceNumber} from {merchantName}',
    defaultBody: 'This is a friendly reminder that your invoice is past due. Please take a moment to review and submit your payment at your earliest convenience.',
    apiCall: (invoiceNumber, payload) => invoiceService.sendReminder(invoiceNumber, payload),
    errorPrefix: 'Failed to send reminder email'
  }
};

const SendInvoiceModal = ({ isOpen, onClose, invoice, onSuccess, mode = 'invoice' }) => {
  const { company } = useCompany();
  const { branding, occupancy } = useBranding();
  const config = MODE_CONFIG[mode] || MODE_CONFIG.invoice;
  const daysOverdue = useMemo(
    () => calendarDaysOverdue(invoice?.dueDate, company?.timezone),
    [invoice?.dueDate, company?.timezone]
  );
  const ModeIcon = config.IconComponent;

  // All hooks called first, same order every render (patterns.md compliance)
  const [toEmail, setToEmail] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [bccEmails, setBccEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendCopy, setSendCopy] = useState(false);
  const [attachPdf, setAttachPdf] = useState(mode === 'invoice');
  const [logoUrl, setLogoUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // Initial values for dirty tracking
  const [initialSubject, setInitialSubject] = useState('');
  const [initialBody, setInitialBody] = useState('');
  const [fromEmail, setFromEmail] = useState(
    () => resolveComposeFromEmail({ occupancy, company })
  );

  const cancelCloseRef = useRef(null);
  const prevOpenRef = useRef(false);
  const sendResultRef = useRef(null);

  // useMemo for dirty tracking (enterprise pattern)
  const hasUnsavedChanges = useMemo(() => {
    return subject !== initialSubject || body !== initialBody || ccEmails.trim() !== '' || bccEmails.trim() !== '';
  }, [subject, body, ccEmails, bccEmails, initialSubject, initialBody]);

  // Token map for preview interpolation
  const tokenMap = useMemo(() => buildTokenMap(invoice, company), [invoice, company]);

  // From address (read-only, resolved server-side; fromEmail loaded from platform config)
  const fromDisplay = `"${company?.businessName || 'Your Business'}" <${fromEmail}>`;

  // ---------- Load template defaults ----------

  const loadTemplate = useCallback(async () => {
    try {
      setTemplateLoading(true);
      const [result, logoSetting] = await Promise.all([
        outreachService.getTemplate(config.templateKey),
        settingsService.getSetting('email_sender.logo_url').catch(() => null)
      ]);
      const val = result.currentValue || result.defaultValue || {};

      const subj = val.subject || config.defaultSubject;
      const bod = val.body || config.defaultBody;
      setSubject(subj);
      setBody(bod);
      setInitialSubject(subj);
      setInitialBody(bod);
      if (result.fromEmail) setFromEmail(result.fromEmail);
      if (logoSetting?.value) setLogoUrl(logoSetting.value);
    } catch {
      const subj = config.defaultSubject;
      const bod = config.defaultBody;
      setSubject(subj);
      setBody(bod);
      setInitialSubject(subj);
      setInitialBody(bod);
    } finally {
      setTemplateLoading(false);
    }
  }, [config.templateKey, config.defaultSubject, config.defaultBody]);

  // Initialize only on open transition (false → true), not on prop changes while open
  useEffect(() => {
    const justOpened = isOpen && !prevOpenRef.current;
    prevOpenRef.current = isOpen;

    if (justOpened && invoice) {
      sendResultRef.current = null;
      setToEmail(invoice.customerEmail || invoice.customer?.email || '');
      setCcEmails('');
      setBccEmails('');
      setError('');
      setSuccess(false);
      setSending(false);
      setShowPreview(false);
      setShowConfirmClose(false);
      setAttachPdf(mode === 'invoice');
      setFromEmail(resolveComposeFromEmail({ occupancy, company }));
      loadTemplate();
    }
  }, [isOpen, invoice, loadTemplate, mode, occupancy, company]);

  // ---------- Auto-close on success ----------

  const closeWithResult = useCallback(() => {
    if (onSuccess && sendResultRef.current) {
      onSuccess(sendResultRef.current);
    }
    onClose();
  }, [onSuccess, onClose]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(closeWithResult, 2000);
    return () => clearTimeout(timer);
  }, [success, closeWithResult]);

  // ---------- Close handlers (enterprise pattern) ----------

  const handleClose = () => {
    if (success) {
      closeWithResult();
      return;
    }
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleForceClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  // ---------- Send handler ----------

  const handleSend = async () => {
    if (!toEmail.trim()) {
      setError('Recipient email is required');
      return;
    }

    try {
      setSending(true);
      setError('');

      const payload = {
        toEmail: toEmail.trim(),
        ...(ccEmails.trim() && { ccEmails: ccEmails.split(',').map(e => e.trim()).filter(Boolean) }),
        ...(bccEmails.trim() && { bccEmails: bccEmails.split(',').map(e => e.trim()).filter(Boolean) }),
        subject,
        body,
        sendCopy,
        attachPdf
      };

      const result = await config.apiCall(invoice.invoiceNumber, payload);

      sendResultRef.current = result;
      setSending(false);
      setSuccess(true);
    } catch (err) {
      console.error(`Error sending ${mode}:`, err);
      setError(err.response?.data?.error || config.errorPrefix);
      setSending(false);
    }
  };

  // Early returns AFTER all hooks (patterns.md compliance)
  if (!invoice) return null;

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          {/* Backdrop with blur */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className={`relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all duration-500 w-full max-h-[90vh] flex flex-col ${success ? 'max-w-sm' : 'max-w-4xl'}`}>
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 ${config.iconBg} rounded-lg`}>
                        <ModeIcon className={`w-5 h-5 ${config.iconColor}`} />
                      </div>
                      <div>
                        <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                          {config.title}
                        </Dialog.Title>
                        <p className="text-sm text-gray-500">
                          {invoice.invoiceNumber} to {invoice.customerName || invoice.customer?.name || 'customer'}
                          {mode === 'reminder' && daysOverdue > 0 && (
                            <span className="text-amber-600 font-medium">
                              {' '}&mdash; {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue
                            </span>
                          )}
                          {mode === 'reminder' && daysOverdue === 0 && (
                            <span className="text-amber-600 font-medium"> &mdash; due today</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleClose}
                      aria-label={`Close ${config.title.toLowerCase()} modal`}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Scrollable Content */}
                  <div className="flex-1 overflow-y-auto">
                    {/* Success state */}
                    {success ? (
                      <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                          <div className="mx-auto w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-8 h-8 text-green-600" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">{config.successTitle}</h3>
                          <p className="text-sm text-gray-500">
                            {invoice.invoiceNumber} sent to {toEmail}
                          </p>
                        </div>
                      </div>
                    ) : templateLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="text-center">
                          <div className={`animate-spin w-8 h-8 border-4 ${config.spinnerBorder} border-t-transparent rounded-full mx-auto mb-3`} />
                          <p className="text-sm text-gray-500 font-medium">Loading template...</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                        {/* ====== Left Panel — Email Controls ====== */}
                        <div className="p-6 space-y-4">
                          {/* Error */}
                          {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <p className="text-sm text-red-700">{error}</p>
                            </div>
                          )}

                          {/* From (read-only) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                              {fromDisplay}
                            </div>
                          </div>

                          {/* To */}
                          <div>
                            <label htmlFor="send-to-email" className="block text-sm font-medium text-gray-700 mb-1">
                              To <span className="text-red-500">*</span>
                            </label>
                            <input
                              id="send-to-email"
                              type="email"
                              value={toEmail}
                              onChange={(e) => setToEmail(e.target.value)}
                              placeholder="customer@example.com"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                            />
                          </div>

                          {/* CC */}
                          <div>
                            <label htmlFor="send-cc-emails" className="block text-sm font-medium text-gray-700 mb-1">CC</label>
                            <input
                              id="send-cc-emails"
                              type="text"
                              value={ccEmails}
                              onChange={(e) => setCcEmails(e.target.value)}
                              placeholder="cc1@example.com, cc2@example.com"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                            />
                            <p className="text-xs text-gray-400 mt-1">Separate multiple addresses with commas</p>
                          </div>

                          {/* BCC */}
                          <div>
                            <label htmlFor="send-bcc-emails" className="block text-sm font-medium text-gray-700 mb-1">BCC</label>
                            <input
                              id="send-bcc-emails"
                              type="text"
                              value={bccEmails}
                              onChange={(e) => setBccEmails(e.target.value)}
                              placeholder="bcc@example.com"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                            />
                          </div>

                          {/* Subject */}
                          <div>
                            <label htmlFor="send-subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                            <input
                              id="send-subject"
                              type="text"
                              value={subject}
                              onChange={(e) => setSubject(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                            />
                          </div>

                          {/* Body */}
                          <div>
                            <label htmlFor="send-body" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                            <textarea
                              id="send-body"
                              value={body}
                              onChange={(e) => setBody(e.target.value)}
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand resize-y"
                            />
                          </div>

                          {/* Send me a copy */}
                          <label htmlFor="send-copy-toggle" className="flex items-center gap-2 cursor-pointer">
                            <input
                              id="send-copy-toggle"
                              type="checkbox"
                              checked={sendCopy}
                              onChange={(e) => setSendCopy(e.target.checked)}
                              className="rounded border-gray-300 text-brand focus:ring-brand"
                            />
                            <div className="flex items-center gap-1.5">
                              <Copy className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">Send me a copy</span>
                            </div>
                          </label>

                          {/* Attach Invoice PDF */}
                          <label htmlFor="attach-pdf-toggle" className="flex items-center gap-2 cursor-pointer">
                            <input
                              id="attach-pdf-toggle"
                              type="checkbox"
                              checked={attachPdf}
                              onChange={(e) => setAttachPdf(e.target.checked)}
                              className="rounded border-gray-300 text-brand focus:ring-brand"
                            />
                            <div>
                              <span className="text-sm text-gray-700">Attach Invoice PDF</span>
                              <p className="text-xs text-gray-400">Include a PDF copy of the invoice as an attachment</p>
                            </div>
                          </label>

                          {/* Mobile preview toggle */}
                          <button
                            type="button"
                            onClick={() => setShowPreview(prev => !prev)}
                            className="lg:hidden w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                          >
                            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            {showPreview ? 'Hide Preview' : 'Show Preview'}
                          </button>
                        </div>

                        {/* ====== Right Panel — Live Preview ====== */}
                        <div className={`${showPreview ? 'block' : 'hidden'} lg:block bg-gray-100 p-6`}>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Email Preview</h4>

                          <div className="max-w-sm mx-auto bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            {/* Header */}
                            <div className="px-5 pt-5 pb-3 text-center">
                              {logoUrl ? (
                                <img src={logoUrl} alt={company?.businessName || 'Logo'} className="max-h-12 mx-auto mb-2" />
                              ) : (
                                <h2 className="text-base font-bold text-gray-900">
                                  {company?.businessName || 'Your Business'}
                                </h2>
                              )}
                            </div>

                            {/* Subject preview */}
                            <div className="px-5 pb-2">
                              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Subject</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {interpolate(subject, tokenMap) || 'Your subject line will appear here'}
                              </p>
                            </div>

                            {/* Body preview */}
                            <div className="px-5 py-3">
                              <p className="text-sm font-medium text-gray-900 mb-1.5">
                                Hi {tokenMap['{customerName}']},
                              </p>
                              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                                {interpolate(body, tokenMap) || 'Your message will appear here.'}
                              </p>
                            </div>

                            {/* Invoice summary — mode-aware content */}
                            <div className="px-5 py-3 border-t border-gray-100">
                              {mode === 'reminder' ? (
                                <>
                                  {/* Overdue badge */}
                                  <div className="text-center mb-3">
                                    <span className="inline-block px-3 py-1 bg-red-50 border border-red-200 rounded-full text-xs font-semibold text-red-600 uppercase tracking-wide">
                                      {daysOverdue > 0
                                        ? `${daysOverdue} day${daysOverdue !== 1 ? 's' : ''} overdue`
                                        : 'Due today'}
                                    </span>
                                  </div>

                                  {/* Summary table */}
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-500">Invoice</span>
                                    <span className="font-medium text-gray-900">{invoice.invoiceNumber}</span>
                                  </div>
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-500">Amount Due</span>
                                    <span className="font-bold text-gray-900 text-base">{formatCurrency(invoice.totalAmount || 0)}</span>
                                  </div>
                                  {invoice.dueDate && (
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-500">Due Date</span>
                                      <span className="font-semibold text-red-600">{tokenMap['{dueDate}']}</span>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  {/* Invoice mode — line items + summary */}
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-500">Invoice</span>
                                    <span className="font-medium text-gray-900">{invoice.invoiceNumber}</span>
                                  </div>
                                  {invoice.dueDate && (
                                    <div className="flex justify-between text-sm mb-1">
                                      <span className="text-gray-500">Due Date</span>
                                      <span className="font-medium text-gray-900">{tokenMap['{dueDate}']}</span>
                                    </div>
                                  )}

                                  {/* Line items */}
                                  {invoice.items && invoice.items.length > 0 && (
                                    <table className="w-full text-xs mt-3">
                                      <thead>
                                        <tr className="border-b border-gray-200">
                                          <th className="text-left py-1 text-gray-500 font-medium">Item</th>
                                          <th className="text-right py-1 text-gray-500 font-medium">Qty</th>
                                          <th className="text-right py-1 text-gray-500 font-medium">Price</th>
                                        </tr>
                                      </thead>
                                      <tbody className="text-gray-600">
                                        {invoice.items.slice(0, 5).map((item, idx) => (
                                          <tr key={idx} className="border-b border-gray-50">
                                            <td className="py-1 truncate max-w-[120px]">{item.itemName || item.description}</td>
                                            <td className="text-right py-1">{item.quantity}</td>
                                            <td className="text-right py-1">{formatCurrency(item.unitPrice || 0)}</td>
                                          </tr>
                                        ))}
                                        {invoice.items.length > 5 && (
                                          <tr>
                                            <td colSpan={3} className="py-1 text-gray-400 text-center">
                                              +{invoice.items.length - 5} more items
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  )}

                                  <div className="flex justify-between mt-2 pt-2 border-t border-gray-200 text-sm font-bold text-gray-900">
                                    <span>Total Due</span>
                                    <span>{formatCurrency(invoice.totalAmount || 0)}</span>
                                  </div>
                                </>
                              )}
                            </div>

                            {/* Pay Now button */}
                            <div className="px-5 py-4 text-center">
                              <div
                                className="inline-block px-6 py-2 text-white text-sm font-semibold rounded-md"
                                style={{ backgroundColor: branding.colors?.primaryColor || '#18181b' }}
                              >
                                Pay Now
                              </div>
                            </div>

                            {/* Footer */}
                            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 text-center">
                              <p className="text-xs text-gray-500 font-medium">{company?.businessName || 'Your Business'}</p>
                              {company?.addressLine1 && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {company.addressLine1}{company.addressLine2 ? `, ${company.addressLine2}` : ''}{company.city ? `, ${company.city}` : ''}{company.state ? `, ${company.state}` : ''} {company.postalCode || ''}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">Powered by {branding.identity?.platformName || 'Lynx'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sticky Footer — Actions (hidden during success) */}
                  {!templateLoading && !success && (
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={sending}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSend}
                        disabled={sending || !toEmail.trim()}
                        className={`flex items-center gap-2 px-6 py-2 ${config.buttonBg} text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {sending ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            {config.buttonLabel}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Unsaved Changes Confirm Dialog */}
      <Transition.Root show={showConfirmClose} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowConfirmClose(false)} initialFocus={cancelCloseRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-6 py-5 shadow-2xl transition-all w-full max-w-md">
                  <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                    Discard Email?
                  </Dialog.Title>
                  <p className="mt-2 text-sm text-gray-500">
                    You've made changes to this email. Are you sure you want to close without sending?
                  </p>
                  <div className="mt-5 flex justify-end gap-3">
                    <button
                      ref={cancelCloseRef}
                      onClick={() => setShowConfirmClose(false)}
                      className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Keep Editing
                    </button>
                    <button
                      onClick={handleForceClose}
                      className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-500 transition-colors"
                    >
                      Discard
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default SendInvoiceModal;
